import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Address, CreateClientAddressDto, UpdateClientAddressDto } from "@/lib/api";
import { getCustomerRegistrationErrorMessage } from "@/lib/customerRegistrationError";

/** Endpoints de endereço de um dono (cliente CNPJ ou pessoa física): clientAddressesApi e physicalClientAddressesApi. */
export interface AddressBookApi<T extends Address> {
  list: (ownerId: number) => Promise<{ data: T[] }>;
  create: (ownerId: number, address: CreateClientAddressDto) => Promise<T>;
  update: (ownerId: number, addressId: number, address: UpdateClientAddressDto) => Promise<T>;
  setDefault: (ownerId: number, addressId: number) => Promise<T>;
  deactivate: (ownerId: number, addressId: number) => Promise<T>;
}

interface UseAddressBookOptions<T extends Address> {
  queryKey: readonly unknown[];
  ownerId?: number;
  api: AddressBookApi<T>;
  /** Outras queries a invalidar a cada alteração (ex.: o cadastro do dono). */
  invalidateKeys?: readonly (readonly unknown[] | undefined)[];
}

export interface AddressBook<T extends Address = Address> {
  addresses: T[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => unknown;
  createAddress: (address: CreateClientAddressDto) => Promise<T>;
  updateAddress: (args: { addressId: number; address: UpdateClientAddressDto }) => Promise<T>;
  setDefault: (addressId: number) => void;
  deactivate: (addressId: number) => void;
  isSaving: boolean;
  pendingAddressId?: number;
}

/** Endereços de um dono e as ações sobre eles, com as mesmas mensagens para CNPJ e pessoa física. */
export function useAddressBook<T extends Address>({
  queryKey,
  ownerId,
  api,
  invalidateKeys = [],
}: UseAddressBookOptions<T>): AddressBook<T> {
  const queryClient = useQueryClient();

  const query = useQuery<T[]>({
    queryKey,
    queryFn: async () => (await api.list(ownerId!)).data ?? [],
    enabled: !!ownerId,
    staleTime: 0,
  });

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey }),
      ...invalidateKeys.map((key) => (key ? queryClient.invalidateQueries({ queryKey: key }) : Promise.resolve())),
    ]);

  // Criar e editar devolvem a promise para o formulário mostrar os erros de campo; o toast de erro fica com ele.
  const createMutation = useMutation({
    mutationFn: (address: CreateClientAddressDto) => api.create(ownerId!, address),
    onSuccess: async () => {
      toast.success("Endereço cadastrado.");
      await invalidate();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ addressId, address }: { addressId: number; address: UpdateClientAddressDto }) =>
      api.update(ownerId!, addressId, address),
    onSuccess: async () => {
      toast.success("Endereço atualizado.");
      await invalidate();
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (addressId: number) => api.setDefault(ownerId!, addressId),
    onSuccess: async () => {
      toast.success("Endereço padrão alterado.");
      await invalidate();
    },
    onError: (error) => {
      toast.error(getCustomerRegistrationErrorMessage(error, "Não foi possível alterar o endereço padrão."));
      void invalidate();
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (addressId: number) => api.deactivate(ownerId!, addressId),
    onSuccess: async () => {
      toast.success("Endereço desativado.");
      await invalidate();
    },
    onError: (error) => {
      toast.error(getCustomerRegistrationErrorMessage(error, "Não foi possível desativar o endereço."));
    },
  });

  return {
    addresses: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    createAddress: createMutation.mutateAsync,
    updateAddress: updateMutation.mutateAsync,
    setDefault: setDefaultMutation.mutate,
    deactivate: deactivateMutation.mutate,
    isSaving: createMutation.isPending || updateMutation.isPending,
    pendingAddressId: setDefaultMutation.isPending
      ? setDefaultMutation.variables
      : deactivateMutation.isPending
        ? deactivateMutation.variables
        : undefined,
  };
}
